import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { type ReactElement, type SyntheticEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { driversQueryOptions } from 'screens/DriverManager/DriverManager.service'
import type { DriverData } from 'screens/DriverManager/DriverManager.types'
import { assignDriver } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from './TripDashboard.types'

export interface DriverAssignmentFieldProps {
  trip: TripData,
  onChange?: (updatedTrip: TripData) => void
}

const DriverAssignmentField = ({
  trip,
  onChange,
}: DriverAssignmentFieldProps): ReactElement => {
  const { t } = useTranslation()
  const driversQuery = useQuery(driversQueryOptions)
  const assignDriverMutation = useMutation({
    mutationFn: ({ tripCid, driverCid }: { tripCid: string, driverCid: string }) =>
      assignDriver(tripCid, { driverCid }),
  })

  const handleChange = useCallback(async (_event: SyntheticEvent, newValue: DriverData): Promise<void> => {
    await assignDriverMutation.mutateAsync({ tripCid: trip.entityId, driverCid: newValue.cid })
    onChange?.({
      ...trip,
      assignedDriverId: newValue.cid,
      assignedDriver: newValue,
    })
    analyticsEvent('polestar_cs_driver_assigned', [getRouteName(location.pathname)])
  }, [onChange, trip])

  return (
    <Autocomplete
      options={driversQuery.data ?? []}
      disableClearable
      disableListWrap
      fullWidth
      size="small"
      getOptionKey={(driver) => driver.cid}
      getOptionLabel={(driver) => driver.name}
      value={trip.assignedDriver}
      onChange={handleChange}
      renderOption={(props, driver) => (
        <li {...props} key={driver.cid}>
          {driver.name}
        </li>
      )}
      renderInput={(params): ReactElement => (
        <TextField {...params} placeholder={t('screens.cs.trip_dashboard.card.actions.select_driver')} />
      )}
      isOptionEqualToValue={(option, value): boolean => option.cid === value.cid}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          sx: { width: 'auto !important' },
        },
      }}
    />
  )
}

export default DriverAssignmentField